import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface KIInitScreenProps {
  onComplete: () => void;
}

const loadingStages = [
  'Synchronizing Base 44 Engine...',
  'Accessing Ancient Archives...',
  'Calibrating Frequency Resonance...',
  'Ready.',
];

export function KIInitScreen({ onComplete }: KIInitScreenProps) {
  const [stage, setStage] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const stageInterval = setInterval(() => {
      setStage((prev) => {
        if (prev === loadingStages.length - 1) {
          setTimeout(() => {
            setIsExiting(true);
            setTimeout(onComplete, 800);
          }, 1500);
          return prev;
        }
        return prev + 1;
      });
    }, 1200);

    return () => clearInterval(stageInterval);
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 bg-stone-void flex flex-col items-center justify-center overflow-hidden"
      initial={{ opacity: 1 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Breathing Logo */}
      <motion.div
        className="mb-20 text-center"
        animate={{ opacity: isExiting ? 0 : 1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.h1
          className="text-6xl font-bold text-gold text-glow breathe"
          animate={{ y: isExiting ? -50 : 0, scale: isExiting ? 1.5 : 1 }}
          transition={{ duration: 0.8 }}
        >
          KI CODE
        </motion.h1>
        <motion.p
          className="text-xs text-stone-light mt-2 tracking-widest"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 0.3 }}
        >
          LINGUISTIC FREQUENCY PROCESSOR
        </motion.p>
      </motion.div>

      {/* Loading Bar with Kinetic Typography */}
      <div className="w-80">
        {/* Progress Bar */}
        <motion.div className="h-1 bg-stone-dark rounded-full overflow-hidden mb-6">
          <motion.div
            className="h-full bg-gradient-to-r from-silk-gold to-silk-cyan"
            initial={{ width: '0%' }}
            animate={{ width: `${((stage + 1) / loadingStages.length) * 100}%` }}
            transition={{ duration: 0.8 }}
          />
        </motion.div>

        {/* Kinetic Typography */}
        <div className="h-6 relative">
          {loadingStages.map((text, idx) => (
            <motion.p
              key={idx}
              className="absolute text-xs text-cyan text-center w-full"
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: stage === idx ? 1 : 0,
                y: stage === idx ? 0 : 10,
              }}
              transition={{ duration: 0.4 }}
            >
              {text}
            </motion.p>
          ))}
        </div>
      </div>

      {/* Subtle ambient animation in background */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{ duration: 20, repeat: Infinity }}
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.03) 0%, transparent 70%)',
          backgroundSize: '200% 200%',
        }}
      />
    </motion.div>
  );
}
